import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Gift, Users, DollarSign, Copy, Check, Share2, Mail, 
  Twitter, Facebook, Link2, Award, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function ReferralProgram() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await apiClient.get('/referral/code');
      setReferralData(response.data);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/signup?ref=${referralData?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareVia = (platform) => {
    const link = `${window.location.origin}/signup?ref=${referralData?.referral_code}`;
    const text = `Join RazientSounds and book amazing artists for your events! Use my referral link:`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      email: `mailto:?subject=${encodeURIComponent('Join RazientSounds!')}&body=${encodeURIComponent(`${text}\n\n${link}`)}`
    };
    
    window.open(urls[platform], '_blank');
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  const referralLink = `${window.location.origin}/signup?ref=${referralData?.referral_code}`;

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4" data-testid="referral-program-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Referral Program</h1>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Invite friends and earn credits! Both you and your friend get $25 credit when they complete their first booking.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-6 text-center">
              <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">${referralData?.total_earned || 0}</p>
              <p className="text-green-300/80 text-sm">Total Earned</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/10 border-violet-500/30">
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 text-violet-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{referralData?.successful_referrals || 0}</p>
              <p className="text-violet-300/80 text-sm">Successful Referrals</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border-blue-500/30">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{referralData?.pending_referrals || 0}</p>
              <p className="text-blue-300/80 text-sm">Pending Referrals</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link Card */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-violet-400" />
              Your Referral Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <div className="flex-1 bg-black/30 rounded-lg px-4 py-3 text-white/60 font-mono text-sm truncate">
                {referralLink}
              </div>
              <Button onClick={copyReferralLink} className="bg-violet-600 hover:bg-violet-500">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-sm">Share via:</span>
              <Button variant="ghost" size="sm" onClick={() => shareVia('twitter')} className="text-white/60 hover:text-blue-400">
                <Twitter className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => shareVia('facebook')} className="text-white/60 hover:text-blue-500">
                <Facebook className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => shareVia('email')} className="text-white/60 hover:text-white">
                <Mail className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Referral Code */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Your Referral Code</p>
                <p className="text-3xl font-bold text-white font-mono tracking-wider">
                  {referralData?.referral_code || 'LOADING'}
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Award className="w-8 h-8 text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                  <Share2 className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-white font-medium mb-1">1. Share Your Link</h3>
                <p className="text-white/50 text-sm">Share your unique referral link with friends and family</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-white font-medium mb-1">2. They Sign Up</h3>
                <p className="text-white/50 text-sm">Your friend creates an account using your link</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-white font-medium mb-1">3. Both Get $25</h3>
                <p className="text-white/50 text-sm">After their first booking, you both receive $25 credit</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        {referralData?.recent_referrals?.length > 0 && (
          <Card className="bg-white/5 border-white/10 mt-8">
            <CardHeader>
              <CardTitle className="text-white">Recent Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referralData.recent_referrals.map((ref, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-white text-sm">{ref.email?.replace(/(.{3}).*@/, '$1***@')}</p>
                        <p className="text-white/40 text-xs">{new Date(ref.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      ref.status === 'completed' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {ref.status === 'completed' ? '+$25' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
